import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { 
  Target, Mail, Phone, Euro, Loader2, Filter, ChevronDown, ChevronUp, Clock, Calendar, Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';

const positions = [
  { value: 'all', label: 'Todas las posiciones' },
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const priorities = [
  { value: 'all', label: 'Todas las prioridades' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
];

const positionColors = {
  GK: 'bg-yellow-500 text-yellow-950',
  DEF: 'bg-blue-500 text-white',
  MID: 'bg-green-500 text-white',
  FWD: 'bg-red-500 text-white',
};

const positionLabels = {
  GK: 'Portera',
  DEF: 'Defensa',
  MID: 'Mediocampista',
  FWD: 'Delantera',
};

const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const priorityLabels = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

const NeedRow = ({ need }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr 
        className="hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
            <div>
              <p className="font-semibold text-slate-800">{need.club_name}</p>
              <p className="text-sm text-slate-500">{need.club_league} • {need.club_country}</p>
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-heading font-bold text-sm ${positionColors[need.position]}`}>
              {need.position}
            </span>
            <div>
              <p className="font-medium text-slate-800">{positionLabels[need.position]}</p>
              {(need.min_age || need.max_age) && (
                <p className="text-sm text-slate-500">
                  {need.min_age && need.max_age
                    ? `${need.min_age}-${need.max_age} años`
                    : need.min_age
                    ? `+${need.min_age} años`
                    : `-${need.max_age} años`}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-700">{need.club_contact_name}</p>
            <a href={`mailto:${need.club_contact_email}`} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
              <Mail className="w-3 h-3" />
              {need.club_contact_email}
            </a>
            {need.club_contact_phone && (
              <a href={`tel:${need.club_contact_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800">
                <Phone className="w-3 h-3" />
                {need.club_contact_phone}
              </a>
            )}
          </div>
        </td>
        <td className="px-6 py-4">
          {need.budget ? (
            <span className="flex items-center gap-1 font-semibold text-slate-800">
              <Euro className="w-4 h-4 text-indigo-500" />
              {new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(need.budget)}
            </span>
          ) : (
            <span className="text-slate-400">A consultar</span>
          )}
        </td>
        <td className="px-6 py-4">
          <span className={`text-xs uppercase tracking-wider font-bold ${
            need.type === 'transfer' ? 'text-lime-600' :
            need.type === 'loan' ? 'text-amber-600' : 'text-indigo-600'
          }`}>
            {need.type === 'transfer' ? 'Transferencia' :
             need.type === 'loan' ? 'Préstamo' : 'Ambos'}
          </span>
        </td>
        <td className="px-6 py-4">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold border ${priorityColors[need.priority]}`}>
            {priorityLabels[need.priority]}
          </span>
        </td>
      </tr>
      
      {/* Expanded details row */}
      {expanded && (
        <tr className="bg-slate-50 border-t border-slate-100">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: Financial details */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Detalles Económicos
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Presupuesto:</span>
                    <span className="font-medium text-slate-800">
                      {need.budget 
                        ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(need.budget)
                        : 'A consultar'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Salario máximo:</span>
                    <span className="font-medium text-slate-800">
                      {need.max_salary 
                        ? `${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(need.max_salary)}/año`
                        : 'A consultar'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Column 2: Requirements */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Requisitos
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Rango de edad:</span>
                    <span className="font-medium text-slate-800">
                      {need.min_age && need.max_age
                        ? `${need.min_age}-${need.max_age} años`
                        : need.min_age
                        ? `Mínimo ${need.min_age} años`
                        : need.max_age
                        ? `Máximo ${need.max_age} años`
                        : 'Sin especificar'}
                    </span>
                  </div>
                  {need.preferred_nationalities && need.preferred_nationalities.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Nacionalidades:</span>
                      <span className="font-medium text-slate-800 text-right">
                        {need.preferred_nationalities.join(', ')}
                      </span>
                    </div>
                  )}
                  {need.deadline && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Fecha límite:
                      </span>
                      <span className="font-medium text-red-600">{need.deadline}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Column 3: Description */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Descripción
                </h4>
                {need.description ? (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{need.description}</p>
                ) : (
                  <p className="text-sm text-slate-400 italic">Sin descripción adicional</p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const AdminNeedsPage = () => {
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    position: 'all',
    priority: 'all',
  });

  useEffect(() => {
    loadNeeds();
  }, [filters]);

  const loadNeeds = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.position !== 'all') params.position = filters.position;
      if (filters.priority !== 'all') params.priority = filters.priority;
      
      const response = await api.get('/admin/needs', { params });
      setNeeds(response.data);
    } catch (error) {
      console.error('Error loading needs:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ position: 'all', priority: 'all' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-needs-page">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
            Todas las Necesidades
          </h1>
          <p className="text-slate-500 mt-1">
            {needs.length} necesidades activas con datos de contacto. Haz clic en una fila para ver más detalles.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              value={filters.position}
              onValueChange={(value) => setFilters({ ...filters, position: value })}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Posición" />
              </SelectTrigger>
              <SelectContent>
                {positions.map((pos) => (
                  <SelectItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters({ ...filters, priority: value })}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                {priorities.map((pri) => (
                  <SelectItem key={pri.value} value={pri.value}>
                    {pri.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters}>
              <Filter className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Needs Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Club</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Busca</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Contacto</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Presupuesto</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Tipo</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Prioridad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {needs.map((need) => (
                    <NeedRow key={need.id} need={need} />
                  ))}
                </tbody>
              </table>
            </div>

            {needs.length === 0 && (
              <div className="p-12 text-center">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No se encontraron necesidades</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminNeedsPage;
